"use client";

import { useMemo } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, PencilLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCartStore, formatPrice, type CartItem } from "@/stores/cart-store";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { guestFacingModifierText } from "@/lib/guest-facing-modifier-description";

interface CartItemRowProps {
  item: CartItem;
  canEdit: boolean;
  // When true, rows animate in on insert. Gated off during the sheet's initial
  // paint so the opening cart doesn't stagger; on for rows added afterward.
  animateEnter?: boolean;
  onEdit: (trigger: HTMLButtonElement) => void;
}

export function CartItemRow({ item, canEdit, animateEnter, onEdit }: CartItemRowProps) {
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);

  const modifierTotal = item.modifiers.reduce(
    (sum, mod) => sum + mod.priceAdjustment,
    0,
  );
  const itemUnitPrice = item.price + modifierTotal;
  const itemTotal = itemUnitPrice * item.quantity;

  const imageUrl = item.photoUrl
    ? optimizeCloudinaryUrl(item.photoUrl, 128)
    : null;

  const groupedModifiers = useMemo(() => {
    const groups = new Map<string, string[]>();

    for (const modifier of item.modifiers) {
      const groupName = guestFacingModifierText(modifier.modifierName);
      const optionName = guestFacingModifierText(modifier.optionName);
      if (!groupName && !optionName) continue;
      const key = groupName || optionName;
      const currentOptions = groups.get(key) ?? [];
      groups.set(key, [...currentOptions, optionName || groupName]);
    }

    return Array.from(groups.entries()).map(([name, optionNames]) => ({
      name,
      optionNames,
    }));
  }, [item.modifiers]);

  return (
    <motion.div
      layout
      initial={animateEnter ? { opacity: 0, y: 8, scale: 0.98 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: -20, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="group relative flex gap-3 rounded-2xl border border-border/40 bg-card p-2.5 transition-all hover:border-border hover:shadow-md active:scale-[0.99] sm:p-3"
    >
      {/* Item image — always render the well so every row reads photo-forward.
          Mirrors the branded initial-letter placeholder used in order-summary. */}
      {imageUrl ? (
        <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-muted ring-1 ring-border/20 shadow-sm">
          <Image
            src={imageUrl}
            alt={item.name}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-110"
            sizes="64px"
          />
        </div>
      ) : (
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-muted/50 ring-1 ring-border/20 shadow-sm transition-colors group-hover:bg-muted">
          <span className="text-base font-black text-muted-foreground sm:text-lg">
            {item.name.charAt(0)}
          </span>
        </div>
      )}

      {/* Item details */}
      <div className="flex flex-1 flex-col justify-between min-w-0 py-0.5">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-3">
            <h4 className="font-bold text-[15px] sm:text-base leading-tight tracking-tight text-foreground line-clamp-2">
              <span className="mr-1 font-bold tabular-nums text-muted-foreground">
                {item.quantity}×
              </span>
              {item.name}
            </h4>
            <span className="font-bold text-[15px] sm:text-base tabular-nums flex-shrink-0 text-foreground">
              {formatPrice(itemTotal)}
            </span>
          </div>

          {/* Customizations & modifiers */}
          {(groupedModifiers.length > 0 || item.specialInstructions) && (
            <div className="space-y-1">
              {groupedModifiers.slice(0, 3).map((modifier) => (
                <p
                  key={modifier.name}
                  className="text-[11px] font-medium leading-relaxed text-muted-foreground"
                >
                  <span className="font-bold text-foreground/80">
                    {modifier.name}:
                  </span>{" "}
                  <span className="line-clamp-2">
                    {modifier.optionNames.join(", ")}
                  </span>
                </p>
              ))}
              {groupedModifiers.length > 3 && (
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                  +{groupedModifiers.length - 3} more selections
                </p>
              )}

              {item.specialInstructions && (
                <div className="rounded-xl bg-muted/50 px-3 py-2">
                  <p className="text-[10px] font-black uppercase tracking-wide text-muted-foreground/80">
                    Kitchen note
                  </p>
                  <p className="mt-1 text-xs italic font-medium leading-relaxed text-muted-foreground/80 line-clamp-2">
                    &ldquo;{item.specialInstructions}&rdquo;
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Inline quantity + actions band — one line, no separate footer row.
            Tap targets stay >= 44px on mobile (h-11) and 40px at sm (never 36). */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-11 w-11 rounded-xl border-border/40 bg-background/50 hover:bg-muted transition-all active:scale-90 sm:h-10 sm:w-10"
              onClick={() => updateQuantity(item.id, item.quantity - 1)}
              aria-label="Decrease quantity"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-9 text-center text-[15px] font-bold tabular-nums">
              {item.quantity}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-11 w-11 rounded-xl border-border/40 bg-background/50 hover:bg-muted transition-all active:scale-90 sm:h-10 sm:w-10"
              onClick={() => updateQuantity(item.id, item.quantity + 1)}
              aria-label="Increase quantity"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 rounded-full text-muted-foreground hover:text-foreground sm:h-10 sm:w-10"
              onClick={(e) => onEdit(e.currentTarget)}
              disabled={!canEdit}
              aria-label={canEdit ? "Edit item" : "Edit unavailable while menu details load"}
            >
              <PencilLine className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11 rounded-full text-destructive/50 transition-colors hover:bg-destructive/10 hover:text-destructive sm:h-10 sm:w-10"
              onClick={() => removeItem(item.id)}
              aria-label="Remove item"
            >
              <Trash2 className="h-4.5 w-4.5" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
